import React, { useState } from 'react';
import axios from 'axios';
import EvidenceModal from './EvidenceModal';
import EvidenceViewerModal from './EvidenceViewerModal';

import config from '../config';


function Outcome({ outcomeId, outcome, gradeId, user, classes, evidence, discipline = 'stem', onEvidenceChange, readOnly, objectiveIdMap }) {
  const [expanded, setExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewerModal, setShowViewerModal] = useState(false);
  const [editingEvidence, setEditingEvidence] = useState(null);
  const [viewingEvidence, setViewingEvidence] = useState(null);
  const [selectedObjectiveIndex, setSelectedObjectiveIndex] = useState(null);

  // Debug: Show total evidence
  console.log('[Outcome] Total evidence passed:', evidence?.length, 'for outcome', outcome?.number, 'objectives:', outcome?.objectives?.length);

  // Helper to get objective indices from numeric IDs
  const getObjectiveIndexForId = (numericId) => {
    const mapping = objectiveIdMap && objectiveIdMap[numericId];
    if (mapping) {
      return mapping.obj_index;
    }
    return null;
  };

  // Count total evidence for this outcome by summing all objectives' evidence
  const totalEvidence = outcome.objectives.reduce((total, obj) => {
    const objEvidence = evidence.filter(e => e.objective_id === obj.id);
    return total + objEvidence.length;
  }, 0);

  const handleAddEvidence = (objectiveIndex) => {
    const objective = outcome.objectives[objectiveIndex];
    console.log('[Outcome] Adding evidence for objective at index', objectiveIndex);
    console.log('  Type:', typeof objective);
    console.log('  Full objective:', JSON.stringify(objective).substring(0, 100));
    console.log('  Is string?', typeof objective === 'string');
    console.log('  Is object with id?', typeof objective === 'object' && objective?.id);
    setSelectedObjectiveIndex(objectiveIndex);
    setEditingEvidence(null);
    setShowModal(true);
  };

  const handleViewEvidence = (ev) => {
    setViewingEvidence(ev);
    setShowViewerModal(true);
  };

  const handleEditEvidence = (ev, objectiveIndex) => {
    setSelectedObjectiveIndex(objectiveIndex);
    setEditingEvidence(ev);
    setShowModal(true);
  };

  const handleDeleteEvidence = async (evidenceId) => {
    try {
      await axios.delete(`/outcomes/api/evidence/${evidenceId}`, { withCredentials: true });
      onEvidenceChange();
    } catch (error) {
      console.error('Failed to delete evidence:', error);
      alert('Failed to delete evidence');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingEvidence(null);
    setSelectedObjectiveIndex(null);
  };

  const handleViewerModalClose = () => {
    setShowViewerModal(false);
    setViewingEvidence(null);
  };

  const handleModalSave = () => {
    setShowModal(false);
    setEditingEvidence(null);
    setSelectedObjectiveIndex(null);
    onEvidenceChange();
  };

  const truncateClassName = (name) => {
    return name.length > 10 ? name.substring(0, 10) + '...' : name;
  };

  // Generate a consistent color based on class name
  const getClassColor = (className) => {
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < className.length; i++) {
      hash = className.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Convert to hue value (0-360)
    const hue = Math.abs(hash % 360);
    
    // Use HSL for consistent, vibrant colors
    // Saturation 65-75%, Lightness 45-55% for good readability
    return `hsl(${hue}, 70%, 50%)`;
  };

  // Determine if text should be white or black based on background luminance
  const getTextColor = (className) => {
    let hash = 0;
    for (let i = 0; i < className.length; i++) {
      hash = className.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash % 360);
    
    // Calculate luminance for the background color
    // For HSL(h, 70%, 50%), we can determine if it's dark
    // Hues around 240 (blue) tend to be darker at 50% lightness
    // Use white text for darker backgrounds
    const isDark = (hue > 200 && hue < 280);
    
    return isDark ? '#ffffff' : '#000000';
  };

  return (
    <div className={`outcome ${expanded ? 'expanded' : ''}`}>
      <div className="outcome-header" onClick={() => setExpanded(!expanded)}>
        <div className="outcome-title">
          <span className="outcome-number">{outcome.number}</span>
          {outcome.title}
        </div>
        <div className="outcome-badges">
          {totalEvidence > 0 && (
            <span className="evidence-badge">{totalEvidence} evidences</span>
          )}
          <span className="outcome-arrow">›</span>
        </div>
      </div>

      {typeof outcome.objectives === 'undefined' && (
        <div style={{color: 'red', padding: '10px'}}>
          ERROR: outcome.objectives is undefined!
        </div>
      )}

      <div className="outcome-details">
        <div className="outcome-objectives">
          {outcome.objectives.map((objective, objIndex) => {
            const objectiveEvidence = evidence.filter(e => {
              // Objectives from API have numeric IDs
              return e.objective_id === objective.id;
            });
            
            console.log(`[Outcome] Objective ID ${objective.id}: ${objectiveEvidence.length} evidence items`, objectiveEvidence.map(e => e.id));

            return (
              <div key={objIndex} className="objective-item">
                <div className="objective-header">
                  <div className="objective-text">
                    <span className="objective-bullet">✓</span>
                    {objective.text}
                      {objectiveEvidence.length > 0 && (
                    <span className="objective-evidence-count">
                      &nbsp; ({objectiveEvidence.length})
                    </span>
                  )}
                  </div>
                
                </div>

                {objectiveEvidence.length > 0 && (
                  <div className="evidence-badges-container">
                    {objectiveEvidence.map((ev) => (
                      <button
                        key={ev.id}
                        className="evidence-badge-btn"
                        style={{ 
                          backgroundColor: getClassColor(ev.class_name),
                          color: getTextColor(ev.class_name),
                        }}
                        onClick={() => handleViewEvidence(ev)}
                        title={`${ev.title} (${ev.class_name})`}
                      >
                        {truncateClassName(ev.class_name)} 
                      </button>
                    ))}
                  </div>
                )}

                {user && !readOnly && (
                  <button
                    className="add-evidence-btn-inline"
                    onClick={() => handleAddEvidence(objIndex)}
                  >
                    + Add Evidence
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {showModal && (
        <EvidenceModal
          objectiveId={outcome.objectives[selectedObjectiveIndex].id}
          objectiveText={outcome.objectives[selectedObjectiveIndex].text}
          classes={classes}
          discipline={discipline}
          evidence={editingEvidence}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}

      {showViewerModal && viewingEvidence && (
        <EvidenceViewerModal
          evidence={viewingEvidence}
          user={user}
          onClose={handleViewerModalClose}
          onEdit={(ev) => {
            let objIndex;
            if (typeof ev.objective_id === 'number') {
              objIndex = getObjectiveIndexForId(ev.objective_id);
            } else if (typeof ev.objective_id === 'string') {
              objIndex = parseInt(ev.objective_id.split('-').pop());
            }
            if (objIndex !== null && objIndex !== undefined) {
              handleEditEvidence(ev, objIndex);
            }
          }}
          onDelete={handleDeleteEvidence}
        />
      )}
    </div>
  );
}

export default Outcome;
